import { Plus } from 'lucide-react'
import { parsePriorityEntry, serializePriorityEntry, type PriorityEntryDraft } from '../priorityPathUtils'
import { PriorityEntryEditor } from './PriorityEntryEditor'

export interface PriorityListProps {
  value: string[]
  onChange: (value: string[]) => void
  documentFieldOptions: string[]
  outputFieldOptions: string[]
}

// Replaces a raw free-text "extracted.*.property_address" ChipInput with an
// ordered list of friendly, pickable entries - the user's own direct
// feedback was that typing that format by hand is confusing and error
// prone. Each entry is still stored as the exact same raw string on the
// wire (parsePriorityEntry/serializePriorityEntry), so OutputResolutionEntry
// and the save payload are completely unchanged - only how it's edited.
export function PriorityList({ value, onChange, documentFieldOptions, outputFieldOptions }: PriorityListProps) {
  const entries = value.map(parsePriorityEntry)

  function updateEntry(index: number, next: PriorityEntryDraft) {
    onChange(value.map((raw, i) => (i === index ? serializePriorityEntry(next) : raw)))
  }

  function removeEntry(index: number) {
    onChange(value.filter((_, i) => i !== index))
  }

  function moveEntry(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= value.length) return
    const next = [...value]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  function addEntry() {
    const defaultEntry: PriorityEntryDraft = documentFieldOptions[0]
      ? { source: 'document_field', value: documentFieldOptions[0] }
      : { source: 'request_case_data', value: '' }
    onChange([...value, serializePriorityEntry(defaultEntry)])
  }

  return (
    <div className="flex flex-col gap-2">
      {entries.length > 1 && (
        <p className="text-meta text-text-secondary">Tried in order, 1 first - the first value found is used.</p>
      )}
      {entries.map((entry, index) => (
        <PriorityEntryEditor
          key={index}
          rank={index + 1}
          entry={entry}
          onChange={(next) => updateEntry(index, next)}
          onRemove={() => removeEntry(index)}
          onMoveUp={index > 0 ? () => moveEntry(index, -1) : undefined}
          onMoveDown={index < entries.length - 1 ? () => moveEntry(index, 1) : undefined}
          documentFieldOptions={documentFieldOptions}
          outputFieldOptions={outputFieldOptions}
        />
      ))}
      <button
        type="button"
        onClick={addEntry}
        className="inline-flex w-fit items-center gap-1 text-meta font-medium text-accent hover:underline"
      >
        <Plus size={14} strokeWidth={1.5} />
        Add priority
      </button>
    </div>
  )
}
